import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Link } from "react-router-dom";

import { api, type Bai, type KetQuaDuyet } from "../api.ts";
import { khoa } from "../lib/khoa.ts";
import { daChoBaoLau, gioVN, nhanTrangThai } from "../lib/nhan.ts";
import { Bao, Chip, DangTai, DanhSach, DauTrang, Hang, Nut, Rong } from "../thanh-phan/giao-dien.tsx";
import { useToast } from "../thanh-phan/toast.tsx";

export function HangCho() {
  const qc = useQueryClient();
  const toast = useToast();

  const { data, isPending, error, refetch } = useQuery({
    queryKey: khoa.hangCho,
    queryFn: api.hangCho,
  });

  /**
   * **Duyệt được thẳng từ đây; TỪ CHỐI thì không.**
   *
   * Không phải quên: từ chối bắt buộc có lý do ≥10 ký tự, và lý do đó là **đầu vào duy nhất của
   * vòng học, không bổ sung hồi tố được**. Một nút "Từ chối" trên danh sách hoặc phải mở hộp
   * thoại nhập lý do — tức là màn Chi tiết thu nhỏ — hoặc phải cho từ chối không lý do, và cái
   * thứ hai làm hỏng vòng học trong im lặng.
   *
   * Duyệt thì khác: nó không cần đầu vào nào, nên bấm thẳng ở đây là an toàn.
   */
  const duyet = useMutation({
    mutationFn: (id: string) => api.duyet(id),
    /**
     * Kết quả đi bằng **toast**, không phải một `<Bao>` chèn giữa trang.
     *
     * Bài duyệt xong biến khỏi danh sách ngay lập tức, nên nếu phản hồi nằm trong thân trang thì
     * nó xuất hiện ở một chỗ vừa mới xô lệch — và trên danh sách dài nó nằm ngoài tầm nhìn. Toast
     * hiện ở đáy màn, cùng phía với ngón tay vừa bấm.
     */
    onSuccess: (kq: KetQuaDuyet) => {
      toast.on(
        `Đã duyệt — ${kq.trangThaiMoi}`,
        <>
          Huỷ <strong>{kq.soJobDaHuy}</strong> job đang chờ · job tiếp theo:{" "}
          {kq.jobTiepTheo ?? "không có"}
          {kq.canhBao.map((c, i) => (
            <p key={i} className="mt-1 text-canh-700">
              ⚠️ {c}
            </p>
          ))}
        </>,
      );
      void qc.invalidateQueries({ queryKey: khoa.hangCho });
      void qc.invalidateQueries({ queryKey: khoa.tongQuan });
    },
    onError: (loi: Error) => toast.loi("Không duyệt được", loi.message),
  });

  return (
    <div className="min-w-0 space-y-4">
      <DauTrang
        tieuDe="Hàng chờ duyệt"
        moTa="Bài chờ lâu nhất đứng trước."
        chiTiet={
          <>
            Ba trạng thái cần anh: chờ duyệt · đã từ chối bài · đã từ chối ảnh. <strong>Từ chối
            phải mở bài ra</strong> — lý do bắt buộc ≥10 ký tự, và nó là đầu vào duy nhất của vòng
            học, không bổ sung lại được sau.
          </>
        }
        trangThai={data ? <Chip muc={data.length > 0 ? "canh" : "on"}>{data.length} bài</Chip> : undefined}
      />

      {error ? (
        <Bao muc="loi" tieuDe="Không tải được">
          {error.message}
          <button onClick={() => refetch()} className="ml-2 underline">
            Thử lại
          </button>
        </Bao>
      ) : isPending ? (
        <DangTai />
      ) : data.length === 0 ? (
        <Rong tieuDe="Không có bài nào chờ duyệt">
          Đây là trạng thái tốt — không phải lỗi, và cũng không phải &ldquo;chưa tải được&rdquo;.
        </Rong>
      ) : (
        <DanhSach>
          {data.map((b: Bai) => {
            const nhan = nhanTrangThai(b.trangThai);
            return (
              <Hang key={b._id}>
                <Link
                  to={`/bai/${b._id}`}
                  className="flex min-w-0 items-start gap-3 px-4 py-3 transition-colors duration-100 hover:bg-muc-100"
                >
                  {/* `min-w-0` ở đây là thứ làm `truncate` bên trong hoạt động (AP-35, AP-76).
                      Bỏ nó thì tiêu đề tiếng Việt dài đẩy cả hàng tràn ngang ở 390px. */}
                  <div className="min-w-0 flex-1">
                    <div className="truncate text-sm font-medium text-muc-900" title={b.title.vi}>
                      {b.title.vi}
                    </div>

                    <div className="mt-1.5 flex min-w-0 flex-wrap items-center gap-1.5">
                      <Chip muc="thuong" title="site đích">
                        {b.siteSlug}
                      </Chip>
                      <Chip muc={nhan.muc} title={nhan.giaiThich}>
                        {nhan.chu}
                      </Chip>
                      {b.title.en !== undefined && <Chip muc="thuong">song ngữ</Chip>}
                      {b.dangFacebook && <Chip muc="thuong">+ Facebook</Chip>}
                      {b.soLanVietLai > 0 && (
                        <Chip muc="canh" title="Trần là 2 lượt — AP-97">
                          viết lại {b.soLanVietLai}/2
                        </Chip>
                      )}
                    </div>

                    <div className="mt-1 flex min-w-0 flex-wrap items-center gap-x-2 text-2xs text-muc-500">
                      <span>chờ {daChoBaoLau(b.updatedAt)}</span>
                      <span className="text-muc-300">·</span>
                      <span>tạo {gioVN(b.createdAt)}</span>
                    </div>

                    {b.lyDoTuChoi !== null && (
                      <div className="mt-1 truncate text-2xs text-loi-600" title={b.lyDoTuChoi}>
                        Lý do từ chối: {b.lyDoTuChoi}
                      </div>
                    )}
                  </div>

                  <span className="shrink-0 self-center text-muc-400" aria-hidden="true">
                    ›
                  </span>
                </Link>

                {/* Nút nằm NGOÀI <Link> — lồng button trong link thì mỗi cú bấm vừa gọi API vừa
                    điều hướng sang trang khác, và người bấm thấy màn hình nhảy đi chứ không thấy
                    kết quả. */}
                <div className="flex shrink-0 flex-wrap gap-2 px-4 pb-3">
                  {b.trangThai === "PENDING_REVIEW" && (
                    <Nut
                      kieu="chinh"
                      // ⚠️ `duyet.isPending` **một mình là sai**: nó đúng cho cả mutation, nên mọi
                      // nút Duyệt trên danh sách cùng quay khi chỉ một bài đang chạy — và người
                      // bấm không còn biết mình vừa bấm cái nào. `variables` giữ đúng id vừa gửi.
                      dangChay={duyet.isPending && duyet.variables === b._id}
                      onClick={() => duyet.mutate(b._id)}
                    >
                      Duyệt
                    </Nut>
                  )}
                  <Link
                    to={`/bai/${b._id}`}
                    className="rounded-btn bg-muc-0 px-3 py-1.5 text-sm font-medium text-muc-700 ring-1 ring-muc-300 transition-[transform,background-color] duration-[var(--nhip-bam)] ease-[var(--ease-ra)] hover:bg-muc-100 active:scale-[0.97]"
                  >
                    Đọc rồi quyết
                  </Link>
                </div>
              </Hang>
            );
          })}
        </DanhSach>
      )}
    </div>
  );
}
